import {
  ChevronDownOutline,
  SmsOutline,
  UserOutline,
  LockOutline as Lock,
  LogoutOutline as Logout,
  PhoneOutline as Phone,
} from '@aslanpay/portal-common-lib';
import {
  HStack,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import useNavbarActions from 'app/hooks/useNavbarActions';
import useAuthContext from 'app/hooks/useAuthContext';

import NotificationMenu from '../NotificationMenu';
import PortalSwitcherMenuItem from '../PortalSwitcherMenuItem';

interface NavbarActionsProps {
  // Renders the actions in white rather than purple, for use over the dark sidebar header.
  onDarkBackground?: boolean;
}

function NavbarActions({ onDarkBackground = false }: NavbarActionsProps) {
  const { activeLoginContext } = useAuthContext();
  const foreground = onDarkBackground ? 'brand.white' : 'brand.purple';
  const {
    user,
    company,
    isSSOUser,
    openModal,
    handleLogout,
    handleProfileClick,
    handleChangePasswordClick,
  } = useNavbarActions();

  const isMobile = useBreakpointValue({ base: true, sm: false }) ?? true;
  const isTablet = useBreakpointValue({ base: false, sm: true, lg: false }) ?? false;

  return (
    <HStack spacing="40px" align="center">
      {activeLoginContext === 'EMPLOYEE' && <NotificationMenu />}
      <Link
        href="mailto:support@aslan.io"
        target="_blank"
        rel="noopener noreferrer"
        display="flex"
        alignItems="center"
        color={foreground}
        textStyle="smallMedium"
        _hover={{ textDecoration: 'none', opacity: 0.8 }}
      >
        <SmsOutline boxSize={isMobile ? '20px' : '16px'} mr={2} />
        {!isMobile && 'Contact Aslan'}
      </Link>

      <Menu>
        <MenuButton
          data-testid="user-menu-button"
          cursor="pointer"
          color={foreground}
          textStyle="smallMedium"
          _hover={{ opacity: 0.8 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UserOutline boxSize={isMobile ? '20px' : '16px'} />
            {!isMobile && !isTablet && (
              <Text>
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.email}
              </Text>
            )}
            <ChevronDownOutline boxSize="16px" />
          </div>
        </MenuButton>
        <MenuList bg="white" border="1px solid" borderColor="brand.lightGray1" boxShadow="lg">
          <MenuItem
            onClick={handleProfileClick}
            icon={<UserOutline boxSize="16px" />}
            color="brand.darkPurple"
            _hover={{ bg: 'brand.lightGray2' }}
          >
            My profile
          </MenuItem>
          {!isSSOUser && (
            <MenuItem
              onClick={handleChangePasswordClick}
              icon={<Lock boxSize="16px" />}
              color="brand.darkPurple"
              _hover={{ bg: 'brand.lightGray2' }}
            >
              Change {activeLoginContext === 'EMPLOYEE' ? 'passcode' : 'password'}
            </MenuItem>
          )}
          {user && !user.phoneNumberVerified && company?.type === 'GIFTING_ONLY' && (
            <MenuItem
              onClick={openModal}
              icon={<Phone boxSize="16px" />}
              color="brand.darkPurple"
              _hover={{ bg: 'brand.lightGray2' }}
            >
              Verify phone number
            </MenuItem>
          )}
          <PortalSwitcherMenuItem />
          <MenuItem
            onClick={handleLogout}
            icon={<Logout boxSize="16px" />}
            color="brand.darkPurple"
            _hover={{ bg: 'brand.lightGray2' }}
          >
            Log out
          </MenuItem>
        </MenuList>
      </Menu>
    </HStack>
  );
}

export default NavbarActions;
